import { Flex, Text } from '@weareberyl/design-system'
import { Tooltip } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { Key } from 'antd/lib/table/interface'

import BooleanIcon from '~/components/shared/BooleanIcon'
import {
  VehicleType,
  ZoneHistoryChangesQuery_zone_history_changes_ZoneHistoryChangesConnection_nodes_ZoneHistoryChanges as ZoneHistoryChangesQueryNode,
} from '~/gql/generated/graphql'
import { formatDatetime, VehicleIcon } from '~/utils'
import { WithIsChangedBool } from './markChangedValues'

const defaultRender = value => value

const alignLookup = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
}

/**
 * Thanks to the markChangedValues helper that's been run over the data, we know
 * that this render function will be given the tuple
 * [originalValue, isModified].
 *
 * Use isModified to wrap the original render function with styling that shows
 * changed values
 */
const highlightChangedValuesRender = (
  record: [React.ReactElement | null, boolean],
  key: Key | undefined,
  render,
  align = 'left',
) => {
  if (!record) return
  const [value, isModified] = record
  const justifyContent = alignLookup[align] ?? 'flex-start'

  if (key === 'time') {
    // skipcq: JS-0045
    return render(value) // Time always changes, don't highlight that
  }

  // skipcq: JS-0045
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent,
        opacity: `${isModified ? '1' : '0.6'}`, // fade out unchanged values
      }}
      // Add class so we can apply some CSS using antd's LESS values
      className={isModified ? 'bay-history-modified-value' : ''}
    >
      {
        render(value) // call the original render function with the original value
      }
    </div>
  )
}

const columns: ColumnsType<WithIsChangedBool<ZoneHistoryChangesQueryNode>> = [
  {
    title: 'Updated At',
    dataIndex: ['time'],
    key: 'time',
    align: 'center',
    render: value => <Text>{formatDatetime(value, 'datetime-minutes')}</Text>,
  },
  {
    title: 'Name',
    dataIndex: ['name'],
    key: 'name',
    render: value => <Text>{value}</Text>,
  },
  {
    title: 'Capacity',
    dataIndex: ['capacity'],
    key: 'capacity',
    align: 'center',
  },
  {
    title: 'Permitted Vehicles',
    dataIndex: ['permitted_vehicles'],
    key: 'permitted_vehicles',
    align: 'center',
    render: (value: VehicleType[]) => {
      return (
        <Flex>
          {value.map(vehicle => (
            <VehicleIcon key={vehicle} vehicle={vehicle} size={21} />
          ))}
        </Flex>
      )
    },
  },
  {
    title: 'Enabled',
    dataIndex: ['is_suspended'],
    key: 'is_suspended',
    align: 'center',
    // Label "Suspended" as "Enabled" and flip the boolean, so that enabled bays have a green tick, and suspended bays have a red cross
    render: value => <BooleanIcon value={!value} />,
  },
  {
    title: 'Promotional',
    dataIndex: ['is_promotional'],
    key: 'is_promotional',
    align: 'center',
    render: value => <BooleanIcon value={value} />,
  },
  {
    title: 'Priority',
    dataIndex: ['is_priority'],
    key: 'is_priority',
    align: 'center',
    render: value => <BooleanIcon value={value} />,
  },
  {
    title: 'Notes',
    dataIndex: ['notes'],
    key: 'notes',
    width: 250,
    render: value => {
      return (
        <Tooltip placement="topLeft" title={value}>
          <span
            className="ant-table-cell-ellipsis"
            style={{ display: 'inline-block', maxWidth: '150px' }}
          >
            {value}
          </span>
        </Tooltip>
      )
    },
  },
]

// Wrap the original render function with the one that highlights changed values
columns.forEach(col => {
  const render = col.render || defaultRender

  col.render = record => {
    return highlightChangedValuesRender(record, col.key, render, col.align)
  }
  return col
})

export default columns
